import get from 'lodash/get';
import compact from 'lodash/compact';
import each from 'lodash/each';
import every from 'lodash/every';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import cloneDeep from 'lodash/cloneDeep';
import uniq from 'lodash/uniq';
import some from 'lodash/some';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import RxSocket from '@Services/RxSocket';
import SocketBroker from '@Services/SocketBroker';
import { RxSocketUrls } from '@Constants/CommonConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import RequestUrls from '@Constants/RequestUrlsConstants';
import MessageBroker from '@Services/MessageBroker';
import { eachHierarchy, everyHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import AppStorage from '@Services/AppStorage';
import * as QueryFileFactory from '@Services/QueryFileFactory';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import StructureOptionsData from '@Services/StructureOptionsData';

export class Validation {
  atLeastOne(value) {
    const result =
      this.atLeastOneValue ||
      this.required(value) ||
      !!get(value, 'exist', false) ||
      !!get(value, 'structures.0.structure', false) ||
      !!get(value, 'historyItems', false) ||
      !!get(value, 'advancedItems', false);

    this.atLeastOneValue = result;
    return result;
  }

  required(value) {
    if (!isString(value)) {
      return false;
    }
    if (!value.length) {
      return false;
    }

    this.atLeastOneValue = true;
    return true;
  }

  isAnyValue() {
    return this.atLeastOneValue;
  }
}

/**
 * @name getImagePath
 * @param {Object} obj
 * @param {Object} token
 * @description get image path
 */
export const getImagePath = (obj, token) => {
  if (!obj.imageUrl) {
    return obj;
  }

  // eslint-disable-next-line no-param-reassign
  obj.imgPath = SimpleTmpl.format(
    RequestUrls.relativeResultsImage.url,
    {
      imgPath: obj.imageUrl,
      accessToken: token,
    },
    true
  );

  return obj;
};

export const subscription = (callback, isAll) => (data) => {
  const payload = get(data, 'payload');
  if (!payload || (!payload.resultName && !isAll)) {
    return;
  }

  getImagePath(payload, AuthHelper.getAccessToken());

  const result = {
    context: payload.context,
    databaseId: payload.databaseId,
    resultName: payload.resultName,
    hitSetKey: payload.hitSetKey,
    resultsCount: payload.resultSize,
    payload,
  };

  if (isFunction(callback)) {
    callback(result);
  }
};

/**
 * @name saveQueryToFile
 * @param {Object} data
 * @description save query to file
 */
export const saveQueryToFile = (data) => {
  return MessageBroker.send(RequestUrls.queryAsFile, data);
};

/**
 * @name openQueryFromLoadedFile
 * @param {Object} file
 * @description open query from loaded file
 */
export const openQueryFromLoadedFile = (file) => {
  return QueryFileFactory.parseFile(file);
};

/**
 * @name sendQuery
 * @param {Object} data
 * @description send query
 */
export const sendQuery = (data) => {
  const clonedData = cloneDeep(data);
  return RxSocket.send(RxSocketUrls.AdvancedQuery, clonedData);
};

/**
 * @name setupWebSockets
 * @param {Object} callback
 * @param {Boolean} isHandleAll
 * @param {Function} errorCallback
 * @description setup web sockets
 */
// eslint-disable-next-line consistent-return
export const setupWebSockets = (callback, isHandleAll, errorCallback) => {
  if (isFunction(errorCallback)) {
    SocketBroker.observe(RxSocketUrls.ChannelsUrls.QueueErrors, errorCallback);
  }

  if (isFunction(callback)) {
    return SocketBroker.observe(RxSocketUrls.ChannelsUrls.SearchInfo, subscription(callback, isHandleAll));
  }
};

/**
 * @name isValidQuery
 * @param {Object}query
 * @description check if query is valid
 */
export const isValidQuery = (query) => {
  let valid = true;
  const validator = new Validation();

  everyHierarchy(
    query,
    (queryItem) => {
      const validateFields = (child, index, container) => {
        const result = every(child.validation, (method) => validator[method](child.value));
        validator.atLeastOne(child.value);

        // eslint-disable-next-line no-param-reassign
        delete child.validation;

        // children array should be length 0 if the child is empty
        if (isEmpty(child)) {
          // eslint-disable-next-line no-param-reassign
          container[index] = undefined;
        }

        return result;
      };

      valid = every(queryItem.fields, validateFields);

      // children array should be length 0 if the child is empty, removing undefined
      if (queryItem.fields) {
        // eslint-disable-next-line no-param-reassign
        queryItem.fields = compact(queryItem.fields);
      }

      validator.atLeastOne(queryItem);
      return valid; // break the recursion if false
    },
    null,
    { children: 'facts' }
  );

  return valid && validator.isAnyValue();
};

/**
 * @name isQueryLetEmpty
 * @param {Object} data
 * @description check if ql is empty
 */
export const isQueryLetEmpty = (data) => {
  return !some(data, (child) => child.value);
};

/**
 * @name getDBList
 * @description get DB list
 */
export const getDBList = () => {
  const nodesList = AppStorage.instance.get('qbNodesList');
  if (!nodesList) {
    return [];
  }
  const DBList = [];
  eachHierarchy(
    nodesList,
    (node) => {
      if (node.type === 'history') {
        DBList.push(node.databaseId);
      }
    },
    null,
    {
      children: 'items',
    }
  );

  return uniq(DBList);
};

/**
 * @name getQueryByWhereClause
 * @param {Object} query
 * @description get query by where clause
 */
export const getQueryByWhereClause = (query) => {
  // RXFE-4517 R1 Reaxys Tree search cannot be edited in Query Builder
  if (
    query.disableQueryEdit ||
    !(get(query, 'whereClauseImportToken', false) || get(query, 'quickSearchItem', false)) // QB or QS
  ) {
    const errorTitle = 'This query type cannot be edited';
    const errorMessage = 'Please note: the query of this history item cannot be edited';
    NotifyDialogsService.openModalWindow({
      title: errorTitle,
      // eslint-disable-next-line no-useless-concat
      text: `${errorMessage}, ` + 'because this query type is no longer supported. You may still open the result set.',
      confirmText: 'OK',
      type: 'info',
      backdrop: 'static',
      trackingOptions: {
        message: `${errorTitle} ${errorMessage}`,
        component: 'Query builder',
      },
    });

    // No need to do anything else
    return Promise.resolve();
  }

  const whereClauseImportToken = query.whereClauseImportToken || query.quickSearchItem.whereClauseImportToken;
  return MessageBroker.send(RequestUrls.importWhereClause, {
    whereClauseImportToken,
    databaseId: query.databaseId,
    ...(query.semanticQuery && { semanticQuery: query.semanticQuery }),
  });
};

const logicOperatorRemoveFn = (hierarchy) => {
  // need to delete 'logicOperator' for the first fact
  if (hierarchy.facts.length > 0 && hierarchy.facts[0].logicOperator && isEmpty(hierarchy.structures)) {
    // eslint-disable-next-line no-param-reassign
    delete hierarchy.facts[0].logicOperator;
    // eslint-disable-next-line no-param-reassign
    delete hierarchy.facts[0].logicOperatorQualifier;
  }
};

const getEmptyFieldsFn = (fields, facts) => {
  const isFieldsExistAndEmpty = fields && isEmpty(fields);
  const isFactsEmpty = isEmpty(facts);
  const isFactsNotExistOrEmpty = !facts || isFactsEmpty;
  const isFactsExistAndEmpty = facts && isFactsEmpty;
  return { isFieldsExistAndEmpty, isFactsNotExistOrEmpty, isFactsExistAndEmpty };
};

export const _removeEmptyFacts = (hierarchy) => {
  if (!hierarchy.facts || isEmpty(hierarchy.facts)) {
    return;
  }

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < hierarchy.facts.length; i++) {
    const node = hierarchy.facts[i];
    const { fields, facts, historyItems } = node;

    const isHistoryItem = historyItems && !isEmpty(historyItems);
    const isStructureItem = get(node, 'structures.0.structure', false);
    if (isHistoryItem || isStructureItem) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (facts && !isEmpty(facts)) {
      _removeEmptyFacts(node);
    }
    const { isFieldsExistAndEmpty, isFactsNotExistOrEmpty, isFactsExistAndEmpty } = getEmptyFieldsFn(fields, facts);

    // remove facts without other facts or with empty fields, but skip facts without fields property
    if ((isFieldsExistAndEmpty && isFactsNotExistOrEmpty) || isFactsExistAndEmpty) {
      hierarchy.facts.splice(i, 1);
      i -= 1;
      logicOperatorRemoveFn(hierarchy);
    }
  }
};

/**
 * @name removeFactsAndFields
 * @methodOf app.RxCommon.AdvancedQuery
 * @param {Object} query
 * @return {Boolean} changed
 * @description remove facts without constraints
 */
export const removeFactsWithoutConstraints = (query) => {
  eachHierarchy(
    query,
    (hierarchy) => {
      _removeEmptyFacts(hierarchy);
    },
    null,
    { children: 'facts' }
  );
};

/**
 * @name _getStructureOptions
 * @param {Object} query
 * @description check structure options, if options are empty then apply defaults
 */
export const checkStructureOptions = (query) => {
  return StructureOptionsData.getDefaultOptions()
    .then((options) => {
      const defaultOptions = options.groups;

      eachHierarchy(
        query.facts,
        (fact) => {
          if (fact.structures && fact.structures.length) {
            each(fact.structures, (structure) => {
              if (structure.structureName) {
                //   This was done ONLY to clean structureName for AA tracking,
                // * should be removed when structureName will no longer be needed for tracking
                // eslint-disable-next-line no-param-reassign
                delete structure.structureName;
              }
              // possible case after import that structure options are empty, need to populate with defaults
              if (!structure.options || isEmpty(structure.options)) {
                // before sending default options need to prepare them in expected format
                // eslint-disable-next-line no-param-reassign
                structure.options = StructureOptionsData.extractOptionValues(StructureOptionsData.getCheckedOptions(defaultOptions));
              }
            });
          }
        },
        null,
        { children: 'facts' }
      );

      return query;
    })
    .catch(Logger.reject);
};
